import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useApiContext from 'hooks/useApiContext';
import { Box, Typography, TextField } from '@mui/material';
import Button from '@mui/material/Button';

interface CopyAdminModalProps {
  adminId: number | string;
  onClose: () => void;
  onSuccess: () => void;
}

function CopyAdminModal({
  adminId,
  onClose,
  onSuccess,
}: CopyAdminModalProps): JSX.Element {
  const { userRepository } = useApiContext();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (): Promise<void> => {
    if (!email || !password) {
      setError(t('users:copy_admin_fields_required'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await userRepository.copyAdmin(adminId, { email, password });
      onSuccess();
    } catch (err: any) {
      setError(err.message || t('users:copy_admin_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: 340 }}>
      <Typography
        sx={{ fontSize: '18px', fontWeight: 700, color: '#111', mb: 1 }}
      >
        {t('users:copy_admin')}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: '#666', mb: 2.5 }}>
        {t('users:copy_admin_description')}
      </Typography>
      <TextField
        label={t('users:email')}
        type="email"
        size="small"
        fullWidth
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label={t('users:password')}
        type="password"
        size="small"
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        sx={{ mb: 2 }}
      />
      {error && (
        <Typography sx={{ fontSize: '13px', color: '#d32f2f', mb: 2 }}>
          {error}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button
          variant="text"
          size="small"
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: 'none', color: '#666' }}
        >
          {t('users:cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            textTransform: 'none',
            bgcolor: '#111',
            borderRadius: '8px',
            '&:hover': { bgcolor: '#333' },
          }}
        >
          {loading ? '...' : t('users:copy_admin_submit')}
        </Button>
      </Box>
    </Box>
  );
}

export default CopyAdminModal;
